/**
 * Shared Zod building blocks. Messages are i18n keys (namespace "common"), translated by the field components.
 * Money is always a decimal string; never z.number() for money.
 */
import { z } from 'zod';

import { isValidUaeIban, normalizeEmiratesId } from '@/shared/lib/identifiers';
import { compare, isDecimalString, isNegative } from '@/shared/lib/money';

export const M = {
  required: 'form.required',
  invalidAmount: 'form.invalidAmount',
  negative: 'form.negativeNotAllowed',
  iban: 'validation.iban',
  mobile: 'validation.mobile',
  emiratesId: 'validation.emiratesId',
  email: 'validation.email',
  tenor: 'validation.tenor',
  date: 'form.invalidDate',
} as const;

export function zMoney(opts: { min?: string; max?: string; allowNegative?: boolean } = {}) {
  return z
    .string({ error: M.required })
    .min(1, M.required)
    .refine(isDecimalString, M.invalidAmount)
    // Zod keeps running refinements after a failed check, so each money comparison guards on a valid decimal first.
    .refine((v) => !isDecimalString(v) || opts.allowNegative || !isNegative(v), M.negative)
    .refine((v) => !isDecimalString(v) || !opts.min || compare(v, opts.min) >= 0, { message: 'form.amountMin', params: { min: opts.min } })
    .refine((v) => !isDecimalString(v) || !opts.max || compare(v, opts.max) <= 0, { message: 'form.amountMax', params: { max: opts.max } });
}

export const zOptionalMoney = (opts: Parameters<typeof zMoney>[0] = {}) => z.union([zMoney(opts), z.null()]).optional();

export const zRequiredString = () => z.string({ error: M.required }).trim().min(1, M.required);

export const zIban = () =>
  z
    .string({ error: M.required })
    .transform((v) => v.replace(/\s/g, '').toUpperCase())
    .refine(isValidUaeIban, M.iban);

export const zUaeMobile = () => z.string({ error: M.required }).regex(/^\+9715\d{8}$/, M.mobile);
/**
 * Emirates ID with or without separators: structure (15 digits starting 784) and check digit, exactly as the API
 * checks it; the value is normalised to 784-YYYY-NNNNNNN-C. One message for both problems (it never names a digit).
 */
export const zEmiratesId = () =>
  z
    .string({ error: M.required })
    .trim()
    .min(1, M.required)
    .refine((v) => normalizeEmiratesId(v) !== null, M.emiratesId)
    .transform((v) => normalizeEmiratesId(v) ?? v);

/** As zEmiratesId, but '' is allowed (edit forms: empty keeps the value on file). */
export const zOptionalEmiratesId = () =>
  z
    .string()
    .trim()
    .refine((v) => v === '' || normalizeEmiratesId(v) !== null, M.emiratesId)
    .transform((v) => (v === '' ? '' : (normalizeEmiratesId(v) ?? v)));
export const zDateOnly = () => z.string({ error: M.required }).regex(/^\d{4}-\d{2}-\d{2}$/, M.date);
